import React, {
  ReactElement, useMemo, useState, useRef, useEffect,
} from 'react'
import {
  Row, Col, Card, Table, Button,
} from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { IBroadcastFormRecipientResponseReportRow } from 'types/BroadcastForm'
import { downloadCSV } from 'utils/csvExport'

interface EnterpriseSurveyReportResponsesProps {
  filteredRecipients: IBroadcastFormRecipientResponseReportRow[]
  sortColumn: string | null
  sortDirection: 'asc' | 'desc'
  onSortChange: (column: string | null, direction: 'asc' | 'desc') => void
}

export const EnterpriseSurveyReportResponses: React.FC<EnterpriseSurveyReportResponsesProps> = ({
  filteredRecipients,
  sortColumn,
  sortDirection,
  onSortChange,
}): ReactElement => {
  const handleSort = (column: string) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      onSortChange(column, sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      // New column, default to ascending
      onSortChange(column, 'asc')
    }
  }

  const renderSortIcon = (column: string) => {
    const isActive = sortColumn === column

    if (isActive) {
      // Show only the active direction icon when sorted
      return (
        <span className="ml-2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
          <FontAwesomeIcon
            icon={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
            style={{ fontSize: '0.75rem', opacity: 1 }}
          />
        </span>
      )
    }

    // Show chevron right when not sorted to indicate sortability
    return (
      <span className="ml-2" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
        <FontAwesomeIcon
          icon="chevron-right"
          style={{ fontSize: '0.75rem', opacity: 1 }}
        />
      </span>
    )
  }

  // Filter to only show rows that have actual question responses
  const questionResponses = useMemo(
    () => filteredRecipients.filter((r) => r.HasResponse && r.QuestionId != null),
    [filteredRecipients],
  )

  // Virtual scrolling state
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 })
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const ROW_HEIGHT = 50
  const CONTAINER_HEIGHT = 600

  // Calculate visible range based on scroll position
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) {
      return undefined
    }

    const handleScroll = () => {
      const { scrollTop } = container
      const start = Math.floor(scrollTop / ROW_HEIGHT)
      const visibleCount = Math.ceil(CONTAINER_HEIGHT / ROW_HEIGHT)
      const end = Math.min(start + visibleCount + 5, questionResponses.length) // +5 for buffer

      setVisibleRange({ start: Math.max(0, start - 2), end }) // -2 for buffer above
    }

    container.addEventListener('scroll', handleScroll)
    handleScroll() // Initial calculation

    return () => {
      container.removeEventListener('scroll', handleScroll)
    }
  }, [questionResponses.length])

  // Get visible rows
  const visibleRows = useMemo(
    () => questionResponses.slice(visibleRange.start, visibleRange.end),
    [questionResponses, visibleRange],
  )

  const metrics = useMemo(() => {
    const totalResponses = questionResponses.length
    const uniqueQuestions = new Set(questionResponses.map((r) => r.QuestionId)).size
    const uniqueRespondents = new Set(
      questionResponses.map((r) => `${r.ProfileId}-${r.FormSessionId}`),
    ).size

    return {
      totalResponses,
      uniqueQuestions,
      uniqueRespondents,
    }
  }, [questionResponses])

  // Helper to format answer text
  const formatAnswer = (response: IBroadcastFormRecipientResponseReportRow): string => {
    if (response.ResponseText != null && response.ResponseText !== '') {
      return response.ResponseText
    }
    if (response.OptionText != null && response.OptionText !== '') {
      if (response.OptionValue != null) {
        return `${response.OptionText} (${response.OptionValue})`
      }
      return response.OptionText
    }
    if (response.OptionValue != null) {
      return String(response.OptionValue)
    }
    return '-'
  }

  // Helper to format sentiment
  const formatSentiment = (response: IBroadcastFormRecipientResponseReportRow): string => {
    const sentiment = (response as any).Sentiment
    if (!sentiment) {
      return '-'
    }
    return sentiment
  }

  const handleDownloadCSV = () => {
    const headers = ['Respondent', 'Form Name', 'Question', 'Answer', 'Scale Type', 'Sentiment', 'Response Date']

    const rows = questionResponses.map((response) => {
      const respondent = response.IsAnonymous
        ? 'Anonymous'
        : `${response.FirstName} ${response.LastName}`.trim()
      const formName = response.Name || ''
      const question = response.QuestionText || ''
      const answer = formatAnswer(response)
      const scaleType = response.ScaleType || ''
      const sentiment = formatSentiment(response)
      const responseDate = response.ResponseAtUtc
        ? new Date(response.ResponseAtUtc).toLocaleString()
        : ''

      return [respondent, formName, question, answer, scaleType, sentiment, responseDate]
    })

    downloadCSV(headers, rows, 'responses')
  }

  return (
    <div>
      <Row className="mt-4 mb-4">
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Total Responses</Card.Title>
              <Card.Text className="h3 mb-0">{metrics.totalResponses.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Unique Questions</Card.Title>
              <Card.Text className="h3 mb-0 text-info">{metrics.uniqueQuestions.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card>
            <Card.Body>
              <Card.Title className="text-muted small mb-2">Respondents</Card.Title>
              <Card.Text className="h3 mb-0 text-success">{metrics.uniqueRespondents.toLocaleString()}</Card.Text>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card>
        <Card.Header>
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Question Responses</h5>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={questionResponses.length === 0}
            >
              <FontAwesomeIcon icon="download" className="mr-2" />
              Download CSV
            </Button>
          </div>
        </Card.Header>
        <Card.Body style={{ padding: 0 }}>
          {questionResponses.length === 0 ? (
            <div className="text-center text-muted p-4">
              No question responses found
            </div>
          ) : (
            <div
              ref={scrollContainerRef}
              style={{
                position: 'relative',
                height: `${CONTAINER_HEIGHT}px`,
                overflow: 'auto',
              }}
            >
              <Table
                bordered
                hover
                responsive
                style={{
                  margin: 0,
                  tableLayout: 'fixed',
                  width: '100%',
                }}
              >
                <colgroup>
                  <col style={{ width: '15%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '20%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '12%' }} />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead style={{
                  position: 'sticky',
                  top: 0,
                  zIndex: 10,
                  backgroundColor: 'white',
                }}
                >
                  <tr>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('respondentName')}
                    >
                      Respondent
                      {renderSortIcon('respondentName')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('formName')}
                    >
                      Form Name
                      {renderSortIcon('formName')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('questionText')}
                    >
                      Question
                      {renderSortIcon('questionText')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('answer')}
                    >
                      Answer
                      {renderSortIcon('answer')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('scaleType')}
                    >
                      Scale Type
                      {renderSortIcon('scaleType')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('sentiment')}
                    >
                      Sentiment
                      {renderSortIcon('sentiment')}
                    </th>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      onClick={() => handleSort('responseDate')}
                    >
                      Response Date
                      {renderSortIcon('responseDate')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {/* Spacer for rows above visible range */}
                  {visibleRange.start > 0 && (
                    <tr style={{ height: `${visibleRange.start * ROW_HEIGHT}px` }}>
                      <td colSpan={7} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                  {/* Visible rows */}
                  {visibleRows.map((response, visibleIndex) => {
                    // Calculate actual index in full dataset for consistent striping
                    const actualIndex = visibleRange.start + visibleIndex
                    const isEven = actualIndex % 2 === 0
                    const rowKey = `${response.ProfileId}-${response.FormSessionId}-${response.QuestionId}-${
                      response.ResponseId || 'no-id'
                    }`
                    return (
                      <tr
                        key={rowKey}
                        className={isEven ? 'table-active' : ''}
                      >
                        <td>
                          {response.IsAnonymous ? (
                            <span className="text-muted">Anonymous</span>
                          ) : (
                            <>
                              {response.FirstName}
                              {' '}
                              {response.LastName}
                            </>
                          )}
                        </td>
                        <td>{response.Name || '-'}</td>
                        <td>{response.QuestionText || '-'}</td>
                        <td>
                          {(() => {
                            if (response.ResponseText != null && response.ResponseText !== '') {
                              return response.ResponseText
                            }
                            if (response.OptionText != null && response.OptionText !== '') {
                              return (
                                <>
                                  {response.OptionText}
                                  {response.OptionValue != null && (
                                    <>
                                      {' '}
                                      (
                                      {response.OptionValue}
                                      )
                                    </>
                                  )}
                                </>
                              )
                            }
                            if (response.OptionValue != null) {
                              return response.OptionValue
                            }
                            return '-'
                          })()}
                        </td>
                        <td>{response.ScaleType || '-'}</td>
                        <td>
                          {(() => {
                            const sentiment = (response as any).Sentiment
                            if (!sentiment) {
                              return '-'
                            }
                            if (sentiment === 'Positive') {
                              return <span className="text-success font-weight-bold">Positive</span>
                            }
                            if (sentiment === 'Neutral') {
                              return <span className="text-warning font-weight-bold">Neutral</span>
                            }
                            if (sentiment === 'Negative') {
                              return <span className="text-danger font-weight-bold">Negative</span>
                            }
                            return sentiment
                          })()}
                        </td>
                        <td>
                          {response.ResponseAtUtc
                            ? new Date(response.ResponseAtUtc).toLocaleString()
                            : '-'}
                        </td>
                      </tr>
                    )
                  })}
                  {/* Spacer for rows below visible range */}
                  {visibleRange.end < questionResponses.length && (
                    <tr style={{ height: `${(questionResponses.length - visibleRange.end) * ROW_HEIGHT}px` }}>
                      <td colSpan={7} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  )
}
